import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Field, useFormikContext, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AccountType } from '../../../shared/Constants';
import { validationMessages } from '../../../i18n/common';
import { DropdownComponent, InputTextComponent } from '../../../components/prime-formik';

const messages = defineMessages({
  fullName: { id: 'attrs.fullName', defaultMessage: 'Full Name' },
  email: { id: 'lbl.email', defaultMessage: 'E-mail address' },
  role: { id: 'attrs.role', defaultMessage: 'Role' },
  member: { id: 'profile.member-role', defaultMessage: 'Member' },
  admin: { id: 'attrs.role.administrator', defaultMessage: 'Administrator' },
  externalSupport: {
    id: 'attrs.role.external-support',
    defaultMessage: 'External Support',
  },
  save: { id: 'attrs.save', defaultMessage: 'Save' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  previous: { id: 'actions.previous', defaultMessage: 'Previous' },
  saveUser: { id: 'actions.save-user', defaultMessage: 'Save user' },
  actions: {
    id: 'menu.item-bank-management.actions',
    defaultMessage: 'Actions',
  },
  ownerAlreadyHasRole: {
    id: 'accept-account-invitation.other-users.owner-cannot-have-additional-role',
    defaultMessage: 'Owner cannot be given an additional role',
  },
});

const getRoleTranslation = (userRole, formatMessage) => {
  switch (userRole) {
    case 'admin':
    case 'nbm-admin':
      return formatMessage(messages.admin);
    case 'external-support':
    case 'nbm-external-support':
      return formatMessage(messages.externalSupport);
    case 'member':
    case 'nbm-member':
      return formatMessage(messages.member);
    default:
      return '';
  }
};

const roleDropdownOptions = (type, availableRoles, formatMessage) => {
  if (type === AccountType.GSC || type === AccountType.GP) {
    return availableRoles
      .filter((e) => e.name !== 'nbm-owner')
      .map((role) => ({
        value: role.id,
        label: getRoleTranslation(role.name, formatMessage),
      }));
  }

  return availableRoles
    .filter((e) => e.name !== 'owner')
    .map((role) => ({
      value: role.id,
      label: role.displayName,
    }));
};

const OtherUsersStep = ({ type, availableRoles }) => {
  const [index, setIndex] = useState(null);

  const history = useHistory();
  const { formatMessage } = useIntl();

  const { setFieldValue, isSubmitting, values: parentFormValues } = useFormikContext();

  const roleOptions = roleDropdownOptions(type, availableRoles, formatMessage);

  const actionsBody = (data, { rowIndex }) => (
    <div className="align-center">
      <Button type="button" className="p-button-secondary" icon="pi pi-pencil" onClick={() => setIndex(rowIndex)} />
    </div>
  );

  const ownerEmail = parentFormValues.owner.email;

  const initialValues =
    index != null
      ? {
          fullName: parentFormValues.additionalUsers[index].fullName,
          email: parentFormValues.additionalUsers[index].email,
          role: parentFormValues.additionalUsers[index].role,
        }
      : {
          fullName: '',
          email: '',
          role: '',
        };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
      .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
      .required(formatMessage(validationMessages.required))
      .default(''),
    email: Yup.string()
      .test({
        message: () => formatMessage(messages.ownerAlreadyHasRole),
        test(value) {
          return ownerEmail !== value;
        },
      })
      .required(formatMessage(validationMessages.required))
      .email(formatMessage(validationMessages.invalidEmail))
      .default(''),
    role: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
  });

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          formikBag.resetForm();

          if (index != null) {
            const updatedValues = parentFormValues.additionalUsers.map((user, userIndex) => {
              if (userIndex === index) {
                return { ...values };
              }

              return user;
            });

            setFieldValue('additionalUsers', updatedValues);
            setIndex(null);
          } else {
            setFieldValue('additionalUsers', [...parentFormValues.additionalUsers, { ...values }]);
          }
        }}
      >
        {({ values, handleSubmit }) => {
          return (
            <>
              <FormattedMessage
                id="accept-account-invitation.users-step.intro"
                defaultMessage="You are the Owner of your organisation’s gr8pi.com account, and thus also an Administrator. However, you may also invite other people as users:"
                tagName="p"
              />
              <FormattedMessage
                id="accept-account-invitation.users-step.member-text"
                defaultMessage="{member} is an internal daily user performing tasks like setting up assessments and surveys, printing reports for interviews etc."
                tagName="p"
                values={{
                  member: <strong>{formatMessage(messages.member)}</strong>,
                }}
              />
              <FormattedMessage
                id="accept-account-invitation.users-step.admin-text"
                defaultMessage="{administrator} is an internal user who, in addition, can manage your organisation’s {gr8piWebsite} account(s), for example, invite other users, create Assessment and Survey Templates and sub-accounts, transfer Credits etc."
                tagName="p"
                values={{
                  administrator: <strong>{formatMessage(messages.admin)}</strong>,
                  gr8piWebsite: <a href="https://gr8pi.com">gr8pi.com</a>,
                }}
              />
              <FormattedMessage
                id="accept-account-invitation.users-step.external-support-text"
                defaultMessage="{externalSupport} is an external user who has the same rights as your internal Administrator(s). Your Great People Inside representative is a typical External Support, but it can be anyone externally supporting your organisation’s use of {gr8piWebsite}. You can deactivate or delete External Support users anytime."
                tagName="p"
                values={{
                  externalSupport: <strong>{formatMessage(messages.externalSupport)}</strong>,
                  gr8piWebsite: <a href="https://gr8pi.com">gr8pi.com</a>,
                }}
              />
              <Form handleSubmit={handleSubmit}>
                <>
                  <div className="p-grid p-fluid p-align-center">
                    <div className="p-col-4">
                      <label>
                        <FormattedMessage id="attrs.fullName" defaultMessage="Full Name" />
                      </label>
                      <Field
                        name="fullName"
                        labelText={formatMessage(messages.fullName)}
                        component={InputTextComponent}
                      />
                    </div>
                    <div className="p-col-4">
                      <label>
                        <FormattedMessage id="lbl.email" defaultMessage="E-mail address" />
                      </label>
                      <Field
                        name="email"
                        type="email"
                        labelText={formatMessage(messages.email)}
                        component={InputTextComponent}
                      />
                    </div>
                    <div className="p-col-4">
                      <label>
                        <FormattedMessage id="attrs.role" defaultMessage="Role" />
                      </label>
                      <Field
                        id="role"
                        name="role"
                        labelText={formatMessage(messages.role)}
                        component={DropdownComponent}
                        filter
                        filterBy="label"
                        options={roleOptions}
                      />
                    </div>
                  </div>
                  <Button type="submit" label={formatMessage(messages.saveUser)} />
                </>
              </Form>
              <br />
              <DataTable value={parentFormValues.additionalUsers} className="text-center">
                <Column field="fullName" header={formatMessage(messages.fullName)} />
                <Column field="email" header={formatMessage(messages.email)} />
                <Column field="role" header={formatMessage(messages.role)} />
                <Column body={actionsBody} header={formatMessage(messages.actions)} />
              </DataTable>
            </>
          );
        }}
      </Formik>
      <br />
      <div>
        <Button type="button" label={formatMessage(messages.previous)} onClick={() => history.goBack()} />
        <Button type="submit" disabled={isSubmitting} label={formatMessage(messages.submit)} />
      </div>
    </>
  );
};

OtherUsersStep.propTypes = {
  type: PropTypes.string.isRequired,
  availableRoles: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default OtherUsersStep;
